import { LocaleStrings } from '../Locale'
import { TranslationItem } from './TranslationTypes'

type Milliseconds = number

interface TranslationsMemoizeMultiLingual {
    timestamp?: Milliseconds
    data?: Record<string, TranslationItem[]> | null
}

/**
 * @description
 * stores memoized translations
 * */
const memoizeAndCacheFetchMultilingualTranslations = () => {
    const memoize: TranslationsMemoizeMultiLingual = {}

    /**
     * @description
     * Fetches translations from the api endpoint for all locales in the LocaleStrings enum
     * and memoizes duplicate requests (deduping)
     * */
    const fetchMultilingualTranslations = async (
        translationsURL: string,
        dedupeInterval: number = 5000
    ): Promise<Record<string, TranslationItem[]> | null | unknown> => {
        if (!!memoize.timestamp && !!memoize.data && memoize.timestamp + dedupeInterval > Date.now()) {
            return memoize.data
        }

        const allTranslations = await _fetchAllTranslations(translationsURL)

        //Set the new memo values
        memoize.timestamp = Date.now()
        memoize.data = allTranslations

        return allTranslations
    }

    /**
     * Handles the fetching of translations for all locales
     */
    const _fetchAllTranslations = async (translationsURL: string) => {
        //Get all locales
        const allLocales = Object.values(LocaleStrings)
        const allTranslations: Record<string, TranslationItem[]> = {}

        //Trigger a 'fetch translations' call for every locale.
        //Collect those calls in a list.
        const translationsCalls = allLocales.map((localeString) => {
            const call = _fetchTranslationsFromServer(`${translationsURL}&locale=${localeString}`)
                .then((response) => {
                    //@ts-ignore
                    if (response) allTranslations[localeString] = response
                })
                .catch((error) => console.error(error))

            return call
        })

        //Wait until all translation fetches have finished
        await Promise.all(translationsCalls).catch(() => {
            return null
        })

        return allTranslations
    }

    const _fetchTranslationsFromServer = async (
        translationsURL: string
    ): Promise<TranslationItem[] | null | unknown> => {
        const response = await fetch(translationsURL, {
            method: 'GET',
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(response.statusText)
        }

        const data = (await response.json())?.data

        return data
    }

    return fetchMultilingualTranslations
}

/** @deprecated Use "Dictionary.fetchMultipleDictionaries" instead */
export const fetchMultilingualTranslations = memoizeAndCacheFetchMultilingualTranslations()
